import { FormEvent, useEffect, useState } from "react"
import { apiFetch } from "../../lib/api"
import { useEmpresa } from "../../app/context/EmpresaContext"

type Plano = {
  codigo: string
  nome: string
  descricao: string | null
  preco: number
  periodo_dias: number
}

type BillingStatus = {
  ativo: boolean
  status: string
  vigencia_fim: string | null
  billing_enforce: boolean
  plano: { codigo: string; nome: string } | null
}

type Checkout = {
  payment_id: string
  qr_code: string | null
  qr_code_base64: string | null
  amount: number
  plano_nome: string
  mp_stub: boolean
}

export const PlanosPage = () => {
  const { empresaId } = useEmpresa()
  const [planos, setPlanos] = useState<Plano[]>([])
  const [status, setStatus] = useState<BillingStatus | null>(null)
  const [checkout, setCheckout] = useState<Checkout | null>(null)
  const [message, setMessage] = useState("")

  const reload = async () => {
    if (!empresaId) return
    const [p, s] = await Promise.all([
      apiFetch<Plano[]>("/v1/planos"),
      apiFetch<BillingStatus>(`/v1/empresas/${empresaId}/billing/status`),
    ])
    setPlanos(p)
    setStatus(s)
  }

  useEffect(() => {
    reload()
  }, [empresaId])

  const handleCheckout = async (e: FormEvent, codigo: string) => {
    e.preventDefault()
    if (!empresaId) return
    setMessage("")
    try {
      const res = await apiFetch<Checkout>(`/v1/empresas/${empresaId}/billing/checkout`, {
        method: "POST",
        body: JSON.stringify({ plano_codigo: codigo }),
      })
      setCheckout(res)
      if (res.mp_stub) {
        setMessage("Modo stub: use Admin → simulate-approve ou configure MP_ACCESS_TOKEN.")
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Erro no checkout")
    }
  }

  useEffect(() => {
    if (!checkout || !empresaId) return
    const timer = setInterval(async () => {
      const res = await apiFetch<{ approved: boolean }>(
        `/v1/empresas/${empresaId}/billing/checkout/${checkout.payment_id}/status`,
      )
      if (res.approved) {
        setMessage("Pagamento confirmado! Plano ativado.")
        setCheckout(null)
        reload()
      }
    }, 8000)
    return () => clearInterval(timer)
  }, [checkout, empresaId])

  const handleCopyPix = async () => {
    if (!checkout?.qr_code) return
    await navigator.clipboard.writeText(checkout.qr_code)
    setMessage("Pix copiado.")
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Planos e assinatura</h1>
        <p className="mt-1 text-sm text-slate-600">Cobrança por CNPJ via Pix Mercado Pago.</p>
      </div>

      {status && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm">
            Status: <strong>{status.ativo ? "Ativo" : status.status}</strong>
            {status.plano && ` — ${status.plano.nome}`}
            {status.vigencia_fim && ` até ${new Date(status.vigencia_fim).toLocaleDateString("pt-BR")}`}
          </p>
          {status.billing_enforce && !status.ativo && (
            <p className="mt-2 text-sm text-amber-700">Emissão bloqueada até contratar um plano.</p>
          )}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {planos.map((plano) => (
          <div key={plano.codigo} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold">{plano.nome}</h2>
            <p className="mt-1 text-sm text-slate-600">{plano.descricao}</p>
            <p className="mt-4 text-2xl font-bold text-brand-600">
              R$ {plano.preco.toFixed(2).replace(".", ",")}
              <span className="text-sm font-normal text-slate-500"> / {plano.periodo_dias} dias</span>
            </p>
            <button
              type="button"
              onClick={(e) => handleCheckout(e, plano.codigo)}
              className="mt-4 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              Pagar com Pix
            </button>
          </div>
        ))}
      </div>

      {checkout && (
        <div className="rounded-xl border border-brand-200 bg-brand-50 p-5">
          <h3 className="font-semibold">Pix — {checkout.plano_nome}</h3>
          <p className="text-sm">Valor: R$ {checkout.amount.toFixed(2).replace(".", ",")}</p>
          {checkout.qr_code_base64 ? (
            <img
              src={`data:image/png;base64,${checkout.qr_code_base64}`}
              alt="QR Code Pix"
              className="mx-auto mt-4 h-48 w-48"
            />
          ) : (
            <p className="mt-2 text-sm text-slate-600">QR stub — configure Mercado Pago ou simule aprovação no admin.</p>
          )}
          {checkout.qr_code && (
            <button type="button" onClick={handleCopyPix} className="mt-3 text-sm text-brand-700 underline">
              Copiar Pix copia e cola
            </button>
          )}
        </div>
      )}

      {message && <p className="text-sm text-slate-700">{message}</p>}
    </div>
  )
}
